// API Configuration
// In production, this will be set via environment variable during build
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ai-translation-api.aicoe-translation-dev.aws.evernorthcloud.com/v1';

// Auth token - set automatically from OKTA authentication.
// `authToken` is kept as a fallback for non-Okta callers (e.g. manual token
// entry in tests).  When the app is running inside the Okta React SDK,
// `tokenGetter` is set to `() => oktaAuth.getAccessToken()` so every request
// fetches the **current** token rather than a snapshot taken at login time.
// This prevents "explicit deny" / 401 errors that occur when Okta silently
// renews the access token after ~1 hour but the stored snapshot has gone stale.
let authToken = '';
let tokenGetter = null;

// Register a live token provider (called from App.js with oktaAuth.getAccessToken).
// Once set, apiCall always uses the getter instead of the static authToken.
export function setTokenGetter(fn) {
  tokenGetter = fn;
}

// Function to set the auth token (called from App.js after OKTA login)
export function setAuthToken(token) {
  authToken = token;
}

// Function to get current auth token
export function getAuthToken() {
  return tokenGetter ? (tokenGetter() || authToken) : authToken;
}

// Check if token is configured
export function hasAuthToken() {
  return !!(tokenGetter ? tokenGetter() : authToken);
}

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  // Always resolve the freshest available token so that Okta silent-renewal
  // is picked up automatically without requiring a page refresh.
  const token = tokenGetter ? (tokenGetter() || authToken) : authToken;
  if (!token) {
    throw new Error('No auth token configured. Please enter a Bearer token.');
  }

  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const err = new Error(data.message || data.error || `API error: ${response.status}`);
    err.status = response.status;
    err.lock = data.lock;
    err.body = data;
    throw err;
  }

  return data;
}

async function lockApiCall(body) {
  return apiCall('/translate', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function lockTranslation({ reqId, resourceType, parentId }) {
  return lockApiCall({
    action: 'lock',
    req_id: reqId,
    resourceType,
    parentId: parentId || undefined,
  });
}

export async function unlockTranslation({ reqId, resourceType, parentId }) {
  return lockApiCall({
    action: 'unlock',
    req_id: reqId,
    resourceType,
    parentId: parentId || undefined,
  });
}

// Submit a new translation request
export async function submitTranslation(data) {
  return apiCall('/translate', {
    method: 'POST',
    body: JSON.stringify({
      source: data.source,
      sourceLanguage: data.sourceLanguage || 'en-US',
      targetLanguage: data.targetLanguage || 'es-US',
      category: data.category || 'general',
      program: data.program || 'domestic',
      tm_id: data.tm_id || '',
    }),
  });
}

// Get status of a specific request
export async function getRequestStatus(reqId) {
  return apiCall(`/translate?req_id=${encodeURIComponent(reqId)}`, {
    method: 'GET',
  });
}

// Get all requests for the current user (filtered by backend based on user/admin status)
export async function getAllRequests() {
  try {
    const response = await apiCall('/translate?list=true&limit=500', {
      method: 'GET',
    });
    
    return response.requests || [];
  } catch (error) {
    console.error('Failed to fetch requests from server:', error);
    
    // Fallback to localStorage method if server list fails
    const storedIds = JSON.parse(localStorage.getItem('translationRequestIds') || '[]');
    
    const requests = await Promise.all(
      storedIds.map(async (id) => {
        try {
          return await getRequestStatus(id);
        } catch (err) {
          return {
            req_id: id,
            status: 'unknown',
            error: err.message,
          };
        }
      })
    );

    return requests.sort((a, b) => {
      const dateA = new Date(a.received_date || 0);
      const dateB = new Date(b.received_date || 0);
      return dateB - dateA;
    });
  }
}

// Store a request ID locally (called after successful submission)
export function storeRequestId(reqId) {
  const storedIds = JSON.parse(localStorage.getItem('translationRequestIds') || '[]');
  if (!storedIds.includes(reqId)) {
    storedIds.unshift(reqId); // Add to beginning
    // Keep only last 100 requests
    const trimmed = storedIds.slice(0, 100);
    localStorage.setItem('translationRequestIds', JSON.stringify(trimmed));
  }
}

// Save translation to Translation Memory (PUT endpoint).
// `original_translation` MUST be forwarded - the backend uses it to
// detect a linguist edit (target != original_translation) and to drive
// the "Linguist edit" attribution tag in the My Translations grids.
// Previously this field was being dropped here, so every edit was
// invisible to the modified-detection logic and rows kept showing
// the neutral "AI" tag even after the linguist had reworded them.
export async function saveToTranslationMemory(data) {
  return apiCall('/translate', {
    method: 'PUT',
    body: JSON.stringify({
      source: data.source,
      sourceLanguage: data.sourceLanguage || 'en-US',
      target: data.target,
      targetLanguage: data.targetLanguage || 'es-US',
      category: data.category || 'linguist-approved',
      program: data.program || 'domestic',
      tm_id: data.tm_id || '',
      notes: data.notes || null,
      req_id: data.req_id || null,
      original_translation: data.original_translation || '',
      error_category: data.error_category || null,
      severity: data.severity || null,
      content_type: data.content_type || '',
    }),
  });
}

export async function saveDraftTranslation(data) {
  return apiCall('/translate', {
    method: 'PUT',
    body: JSON.stringify({
      action: 'save_draft',
      req_id: data.req_id,
      draft_target: data.draft_target,
      content_type: data.content_type || '',
      draft_last_edit_path: data.draft_last_edit_path || '$root',
      draft_last_edit_start: data.draft_last_edit_start,
      draft_last_edit_end: data.draft_last_edit_end,
    }),
  });
}

// ============================================================================
// Bulk Upload Batch Management
// ============================================================================

// Create a new bulk upload batch and get presigned URL for S3 upload.
// `sourceFormat` defaults to 'xlsx' so existing spreadsheet uploads keep
// working without sending the new field. Pass 'json' for JSON document
// uploads - the backend stores it on the batch so the download flow knows
// how to reconstruct the file.
export async function createBulkBatch(fileName, totalCount, sourceFormat = 'xlsx', tmOptions = {}) {
  const {
    tm_id = '',
    targetLanguage = 'es-US',
    program = 'domestic',
    category = 'general',
  } = tmOptions;
  return apiCall('/translate', {
    method: 'POST',
    body: JSON.stringify({
      action: 'create_batch',
      file_name: fileName,
      total_count: totalCount,
      source_format: sourceFormat,
      tm_id,
      targetLanguage,
      program,
      category,
    }),
  });
}

// Get all bulk upload batches for the current user
export async function getBulkBatches() {
  try {
    const response = await apiCall('/translate?batches=true&limit=50', {
      method: 'GET',
    });
    return response.batches || [];
  } catch (error) {
    console.error('Failed to fetch bulk batches:', error);
    return [];
  }
}

// Get requests for a specific batch
export async function getBatchRequests(batchId) {
  try {
    const response = await apiCall(`/translate?batch_id=${encodeURIComponent(batchId)}`, {
      method: 'GET',
    });
    return response.requests || [];
  } catch (error) {
    console.error('Failed to fetch batch requests:', error);
    return [];
  }
}

// Fetch the raw text content of a batch's originally-uploaded file
// (Excel for xlsx batches, raw JSON text for json batches). Used by
// the side JSON viewer in My Translations - Files so the linguist
// sees exactly what they uploaded - no client-side reconstruction
// required, which avoids edge cases where a numeric-string object
// key (e.g. a 9-digit client ID) would be misread as an array
// index by a path-based rebuilder.
export async function getBatchSourceText(batchId) {
  const response = await apiCall(
    `/translate?batch_id=${encodeURIComponent(batchId)}&download=true`,
    { method: 'GET' },
  );
  if (!response.download_url) {
    throw new Error('Download URL not available for this batch');
  }
  const fileResp = await fetch(response.download_url);
  if (!fileResp.ok) {
    throw new Error(`Failed to fetch source file (HTTP ${fileResp.status})`);
  }
  return fileResp.text();
}

// Download the original Excel file for a batch
export async function downloadBatchExcel(batchId, fileName) {
  const response = await apiCall(`/translate?batch_id=${encodeURIComponent(batchId)}&download=true`, {
    method: 'GET',
  });
  
  if (response.download_url) {
    const link = document.createElement('a');
    link.href = response.download_url;
    link.download = fileName || `bulk_upload_${batchId}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    throw new Error('Download URL not available');
  }
}

// Upload a file to S3 using a presigned URL. Content type defaults to
// the Excel mime type for backward compatibility but callers can pass
// 'application/json' (or anything else) for JSON uploads. The S3
// presigned URL was generated with a specific ContentType, so the
// header has to match exactly or S3 will reject the upload.
export async function uploadFileToS3(presignedUrl, file, contentType) {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType
        || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to upload file to S3');
  }

  return true;
}

// Backwards-compatible alias - existing call sites pass an Excel file
// without a content type so they keep working unchanged.
export async function uploadExcelToS3(presignedUrl, file) {
  return uploadFileToS3(presignedUrl, file);
}

// Submit translation with batch context. `sourcePath` is used by JSON
// uploads to record where each leaf value lives in the original
// document so the download flow can rebuild the JSON structure.
export async function submitBatchTranslation(data) {
  return apiCall('/translate', {
    method: 'POST',
    body: JSON.stringify({
      source: data.source,
      sourceLanguage: data.sourceLanguage || 'en-US',
      targetLanguage: data.targetLanguage || 'es-US',
      category: data.category || 'general',
      program: data.program || 'domestic',
      tm_id: data.tm_id || '',
      batch_id: data.batch_id,
      submission_type: 'bulk',
      source_path: data.sourcePath || data.source_path || '',
    }),
  });
}

// ============================================================================
// Translation Memory
// ============================================================================

// Search the curated Translation Memory. Empty `query` returns the first
// `limit` active rows in scan order. The backend caps `limit` at 5000
// (per the export use-case) and returns `truncated: true` when the cap
// is reached so the UI can warn the linguist to refine.
//
// `exact` switches the FilterExpression from substring to equality so
// linguists can isolate a single phrase like "Account" from rows that
// happen to contain that word in a longer string.
export async function searchTranslationMemory(query = '', limit = 200, options = {}) {
  const { exact = false, tm_id = '' } = options;
  const qs = new URLSearchParams({ tm: 'true', limit: String(limit) });
  if (query) qs.set('query', query);
  if (exact) qs.set('exact', 'true');
  if (tm_id) qs.set('tm_id', tm_id);
  try {
    const response = await apiCall(`/translate?${qs.toString()}`, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Failed to search Translation Memory:', error);
    return { items: [], count: 0, truncated: false };
  }
}

// Fetch the full version history for a single English string. Used by
// the row-expansion view in the Translation Memory tab so reviewers
// can see every linguist edit and the comment that came with it.
export async function getTranslationMemoryVersions(english, tmId = '') {
  const qs = new URLSearchParams({ tm: 'true', english });
  if (tmId) qs.set('tm_id', tmId);
  try {
    const response = await apiCall(`/translate?${qs.toString()}`, { method: 'GET' });
    return response.versions || [];
  } catch (error) {
    console.error('Failed to fetch TM versions:', error);
    return [];
  }
}

// ============================================================================
// Phrase Dictionary
// ============================================================================

export const DEFAULT_PHRASE_CATEGORY = 'General (EN -> ES)';

export async function searchPhraseDictionary(query = '', limit = 200, options = {}) {
  const { exact = false, translationMemory = DEFAULT_PHRASE_CATEGORY } = options;
  const qs = new URLSearchParams({
    pd: 'true',
    translationMemory,
    limit: String(limit),
  });
  if (query) qs.set('query', query);
  if (exact) qs.set('exact', 'true');
  try {
    const response = await apiCall(`/translate?${qs.toString()}`, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Failed to search Phrase Dictionary:', error);
    return { items: [], count: 0, truncated: false };
  }
}

export async function getPhraseDictionaryVersions(sourcePhrase, options = {}) {
  const { translationMemory = DEFAULT_PHRASE_CATEGORY } = options;
  const qs = new URLSearchParams({
    pd: 'true',
    translationMemory,
    sourcePhrase,
  });
  try {
    const response = await apiCall(`/translate?${qs.toString()}`, { method: 'GET' });
    return response.versions || [];
  } catch (error) {
    console.error('Failed to fetch Phrase Dictionary versions:', error);
    return [];
  }
}

// Get all requests excluding bulk uploads (for View Requests tab)
export async function getSingleRequests() {
  try {
    const response = await apiCall('/translate?list=true&limit=500&type=single', {
      method: 'GET',
    });
    return response.requests || [];
  } catch (error) {
    console.error('Failed to fetch single requests:', error);
    return [];
  }
}

// Get API-submitted translation requests awaiting linguist review
export async function getReviewQueue() {
  try {
    const response = await apiCall('/translate?review_queue=true&limit=500', {
      method: 'GET',
    });
    return response.requests || [];
  } catch (error) {
    console.error('Failed to fetch review queue:', error);
    return [];
  }
}

// Fetch the TM Config list (active TMs with display names and language pairs).
// Returns an array of TM entries sorted by sort_order.
export async function fetchTmConfig() {
  try {
    const response = await apiCall('/translate?tm_config=true', { method: 'GET' });
    return response.tm_configs || [];
  } catch (error) {
    console.error('Failed to fetch TM config:', error);
    return [];
  }
}

// Admin dashboard metrics (admin-only; returns 403 for non-admins)
export async function getAdminDashboardStats({
  start,
  end,
  user = 'ALL',
  type = 'all',
  tm_id = '',
  targetLanguage = 'ALL',
  limit = 10000,
} = {}) {
  if (!authToken) {
    throw new Error('No auth token configured. Please enter a Bearer token.');
  }

  const qs = new URLSearchParams({
    admin: 'true',
    user,
    type,
    limit: String(limit),
  });
  if (start) qs.set('start', start);
  if (end) qs.set('end', end);
  if (tm_id) qs.set('tm_id', tm_id);
  if (targetLanguage && targetLanguage !== 'ALL') qs.set('targetLanguage', targetLanguage);

  const url = `${API_BASE_URL}/translate?${qs.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });

  if (response.status === 403) {
    const error = new Error('Admin access required');
    error.code = 'ADMIN_FORBIDDEN';
    throw error;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `API error: ${response.status}`);
  }

  return response.json();
}
